// Trailing-window POB rollup. The datasets here are small and hand-authored
// (not a full year of history), so "Monthly/Quarterly/Annual POB" is modeled
// as a trailing-window multiplier on the one real period we do have data
// for — Monthly = actual achieved value, Quarterly = x3, Annual = x12,
// Daily = /30 — rather than fabricating a fake multi-year history. This is
// the same approach the old architecture's POB Dashboard used and disclosed.
export function buildPOBMetrics(orders, target) {
  const active = orders.filter(o => o.currentStage !== 'Cancelled');
  const monthlyAchieved = Math.round(active.reduce((s, o) => s + o.orderValue, 0));
  const achievementPct = target ? Math.round((monthlyAchieved / target) * 100) : 0;

  return {
    daily: { target: Math.round(target / 30), achieved: Math.round(monthlyAchieved / 30) },
    monthly: { target, achieved: monthlyAchieved },
    quarterly: { target: target * 3, achieved: monthlyAchieved * 3 },
    annual: { target: target * 12, achieved: monthlyAchieved * 12 },
    achievementPct,
    productWise: productWiseBreakdown(active),
    pharmacyWise: pharmacyWiseBreakdown(active),
    territoryWise: territoryWiseBreakdown(active),
    trend: monthlyTrend(active),
  };
}

function productWiseBreakdown(orders) {
  const byProduct = {};
  orders.forEach(o => o.lineItems.forEach(li => {
    byProduct[li.productName] = (byProduct[li.productName] || 0) + li.lineValue;
  }));
  return Object.entries(byProduct).map(([productName, value]) => ({ productName, value: Math.round(value) })).sort((a, b) => b.value - a.value);
}

function pharmacyWiseBreakdown(orders) {
  const byPharmacy = {};
  orders.forEach(o => {
    byPharmacy[o.customerId] = byPharmacy[o.customerId] || { customerId: o.customerId, pharmacyName: o.pharmacyName, territory: o.territory || null, value: 0, orderCount: 0 };
    byPharmacy[o.customerId].value += o.orderValue;
    byPharmacy[o.customerId].orderCount += 1;
  });
  return Object.values(byPharmacy).map(p => ({ ...p, value: Math.round(p.value) })).sort((a, b) => b.value - a.value);
}

function territoryWiseBreakdown(orders) {
  const byTerr = {};
  orders.forEach(o => {
    const t = o.territory || 'Unknown';
    byTerr[t] = (byTerr[t] || 0) + o.orderValue;
  });
  return Object.entries(byTerr).map(([territory, value]) => ({ territory, value: Math.round(value) })).sort((a, b) => b.value - a.value);
}

// Real month-grouping from actual order dates — genuine, not a fabricated
// multi-year series, even though it may only span 1-2 months for these
// small hand-authored datasets.
function monthlyTrend(orders) {
  const byMonth = {};
  orders.forEach(o => {
    const m = o.orderDate.slice(0, 7);
    byMonth[m] = (byMonth[m] || 0) + o.orderValue;
  });
  return Object.entries(byMonth).sort(([a], [b]) => a.localeCompare(b)).map(([month, value]) => ({ month, value: Math.round(value) }));
}

// Growth %: splits orders by date into an older half and a more recent half
// (by count, not calendar period, since the dataset is small) and compares
// totals. An approximation given the limited date range available — real
// month-over-month growth needs more history than these datasets carry.
export function computeGrowthPct(orders) {
  const active = [...orders.filter(o => o.currentStage !== 'Cancelled')].sort((a, b) => new Date(a.orderDate) - new Date(b.orderDate));
  if (active.length < 2) return null;
  const mid = Math.floor(active.length / 2);
  const older = active.slice(0, mid);
  const recent = active.slice(mid);
  const olderTotal = older.reduce((s, o) => s + o.orderValue, 0);
  const recentTotal = recent.reduce((s, o) => s + o.orderValue, 0);
  if (olderTotal === 0) return recentTotal > 0 ? null : 0; // "New" if no prior baseline
  return Math.round(((recentTotal - olderTotal) / olderTotal) * 100);
}

export function productMix(orders) {
  const byProduct = {};
  let total = 0;
  orders.forEach(o => o.lineItems.forEach(li => {
    byProduct[li.productName] = (byProduct[li.productName] || 0) + li.lineValue;
    total += li.lineValue;
  }));
  const entries = Object.entries(byProduct).sort((a, b) => b[1] - a[1]);
  const topProduct = entries[0]?.[0] || '—';
  const topProductPct = total && entries[0] ? Math.round((entries[0][1] / total) * 100) : 0;
  return { divisionCount: entries.length, topProduct, topProductPct };
}
